/** Campaign tags from a landing URL, kept in a cookie so signup and checkout can read them. */
export const UTM_COOKIE = 'bounce_utm';

export const UTM_COOKIE_MAX_AGE_SECONDS = 30 * 24 * 60 * 60;

export interface UtmAttribution {
  utmSource: string | null;
  utmMedium: string | null;
  utmCampaign: string | null;
  utmContent: string | null;
}

export const UTM_PARAMS: Record<keyof UtmAttribution, string> = {
  utmSource: 'utm_source',
  utmMedium: 'utm_medium',
  utmCampaign: 'utm_campaign',
  utmContent: 'utm_content',
};

const UTM_KEYS = Object.keys(UTM_PARAMS) as (keyof UtmAttribution)[];

const MAX_VALUE_LENGTH = 200;

const clean = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value.trim().slice(0, MAX_VALUE_LENGTH) : null;

/** The tags in a query string, or null when none of them is present. */
export const readUtmFromSearch = (search: string): UtmAttribution | null => {
  const params = new URLSearchParams(search);
  const attribution = UTM_KEYS.reduce(
    (found, key) => ({ ...found, [key]: clean(params.get(UTM_PARAMS[key])) }),
    {} as UtmAttribution,
  );
  return UTM_KEYS.some((key) => attribution[key]) ? attribution : null;
};

/** The cookie's JSON, tolerant of anything a browser might hand back. */
export const parseUtmCookie = (value: string | undefined): UtmAttribution | null => {
  if (!value) return null;
  try {
    const parsed = JSON.parse(value) as Partial<Record<keyof UtmAttribution, unknown>>;
    const attribution = UTM_KEYS.reduce(
      (found, key) => ({ ...found, [key]: clean(parsed[key]) }),
      {} as UtmAttribution,
    );
    return UTM_KEYS.some((key) => attribution[key]) ? attribution : null;
  } catch {
    return null;
  }
};

export const EMPTY_UTM: UtmAttribution = {
  utmSource: null,
  utmMedium: null,
  utmCampaign: null,
  utmContent: null,
};
